// "Resend code" that counts down until `availableAt` (iOS ResendCodeButton): GoTrue sends at most one code per
// address every 30 s (`max_frequency = "30s"`), so the button says how long to wait instead of failing.

import { clsx } from 'clsx';
import { useEffect, useState } from 'react';
import { Spinner } from './ui';

function secondsUntil(at: number): number {
  return Math.max(0, Math.ceil((at - Date.now()) / 1000));
}

/** Whole seconds until `availableAt` (0 once it has passed), ticking every second while above 0. */
export function useSecondsUntil(availableAt: number): number {
  const [remaining, setRemaining] = useState(() => secondsUntil(availableAt));
  useEffect(() => {
    setRemaining(secondsUntil(availableAt));
    if (availableAt <= Date.now()) return undefined;
    const timer = window.setInterval(() => {
      const next = secondsUntil(availableAt);
      setRemaining(next);
      if (next === 0) window.clearInterval(timer);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [availableAt]);
  return remaining;
}

export function ResendCodeButton({
  availableAt,
  busy,
  disabled,
  onClick,
}: {
  availableAt: number;
  busy?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  const remaining = useSecondsUntil(availableAt);
  const waiting = remaining > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={waiting || busy || disabled}
      aria-live="polite"
      className={clsx(
        'flex w-full items-center justify-center gap-2 py-1 text-sm font-semibold tabular-nums transition',
        waiting || busy ? 'text-slate-400 dark:text-slate-500' : 'text-[var(--gt-orange)] hover:underline',
        'disabled:cursor-not-allowed',
      )}
    >
      {busy && <Spinner className="h-3.5 w-3.5" />}
      {busy ? 'Sending…' : waiting ? `Resend code in ${remaining}s` : 'Resend code'}
    </button>
  );
}
